import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import TaskFormDialog, { RECURRENCE_LABELS } from "@/components/TaskFormDialog";
import { fmtDate } from "@/lib/labels";
import { CheckSquare, Plus, Pencil, Repeat } from "lucide-react";

function isOverdue(t) {
  return t.status !== "ferdig" && t.due_date && t.due_date < new Date().toISOString().slice(0, 10);
}

function TaskList({ params, highlightOverdue, refresh }) {
  const [rows, setRows] = useState([]);
  const [editTask, setEditTask] = useState(null);
  const nav = useNavigate();
  const load = () => api.get(`/tasks?${params}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [params, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "ferdig" ? "apen" : "ferdig" }); load(); };
  const prioCls = { hoy: "text-red-400", normal: "text-muted-foreground", lav: "text-muted-foreground" };
  if (rows.length === 0) return <Empty icon={CheckSquare} title="Ingen oppgaver" />;
  return (
    <div className="space-y-2 mt-4">
      {rows.map((t) => {
        const od = highlightOverdue && isOverdue(t);
        return (
          <div key={t.id} data-testid={`task-${t.id}`} className={`flex items-center gap-3 p-4 rounded-lg bg-card border ${od ? "border-red-500/40" : "border-border"}`}>
            <Checkbox checked={t.status === "ferdig"} onCheckedChange={() => toggle(t)} data-testid={`task-check-${t.id}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm flex items-center gap-2 ${t.status === "ferdig" ? "line-through text-muted-foreground" : ""}`}>
                {t.title}
                {t.recurrence && <span className="inline-flex items-center gap-1 text-[11px] text-blue-400 shrink-0"><Repeat className="w-3 h-3" />{RECURRENCE_LABELS[t.recurrence] || t.recurrence}</span>}
              </p>
              {t.description && <p className="text-xs text-muted-foreground/80 mt-0.5">{t.description}</p>}
              <p className="text-xs text-muted-foreground">
                {t.customer_name && <button className="hover:text-foreground underline" onClick={() => nav(`/kunder/${t.customer_id}`)}>{t.customer_name}</button>}
                {t.due_date && <span className={od ? "text-red-400" : ""}> · Frist {fmtDate(t.due_date)}</span>} · {t.assignee_name}
              </p>
              {t.tagged_names?.length > 0 && (
                <p className="text-[11px] text-blue-400 mt-0.5" data-testid={`task-tags-${t.id}`}>{t.tagged_names.map((n) => `@${n}`).join("  ")}</p>
              )}
            </div>
            <span className={`text-xs font-medium ${prioCls[t.priority] || ""}`}>{t.priority}</span>
            <button onClick={() => setEditTask(t)} data-testid={`task-edit-${t.id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent" title="Rediger"><Pencil className="w-4 h-4" /></button>
          </div>
        );
      })}
      <TaskFormDialog open={!!editTask} onOpenChange={(o) => !o && setEditTask(null)} task={editTask} onSaved={load} />
    </div>
  );
}

function OverdueView({ refresh }) {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignee, setAssignee] = useState("all");
  const [location, setLocation] = useState("all");
  useEffect(() => {
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);
  const parts = ["overdue=true"];
  if (assignee !== "all") parts.push(`assignee_id=${assignee}`);
  if (location !== "all") parts.push(`location=${encodeURIComponent(location)}`);
  const params = parts.join("&");
  return (
    <div>
      <div className="flex flex-wrap gap-3 mt-4">
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid="overdue-assignee-filter" className="w-56 bg-secondary border-border"><SelectValue placeholder="Oppdragsansvarlig" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Alle ansvarlige</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger data-testid="overdue-location-filter" className="w-48 bg-secondary border-border"><SelectValue placeholder="Lokasjon" /></SelectTrigger>
          <SelectContent className="bg-popover border-border">
            <SelectItem value="all">Alle lokasjoner</SelectItem>
            {locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <TaskList params={params} highlightOverdue refresh={refresh} />
    </div>
  );
}

export default function Tasks() {
  const [sp] = useSearchParams();
  const initial = sp.get("overdue") ? "forfalte" : "mine";
  const taskId = sp.get("task");
  const [refresh, setRefresh] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [deepTask, setDeepTask] = useState(null);
  useEffect(() => {
    if (taskId) api.get(`/tasks/${taskId}`).then((r) => setDeepTask(r.data)).catch(() => {});
  }, [taskId]);
  return (
    <Page>
      <PageHeader title="Oppgaver" subtitle="Dine og teamets oppgaver" testid="tasks-header">
        <Button data-testid="new-task-btn" onClick={() => setNewOpen(true)} className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Ny oppgave</Button>
      </PageHeader>
      <TaskFormDialog open={newOpen} onOpenChange={setNewOpen} task={null} onSaved={() => setRefresh((x) => x + 1)} />
      <TaskFormDialog open={!!deepTask} onOpenChange={(o) => !o && setDeepTask(null)} task={deepTask} onSaved={() => { setDeepTask(null); setRefresh((x) => x + 1); }} />
      <Tabs defaultValue={initial}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="mine" data-testid="tab-mine">Mine oppgaver</TabsTrigger>
          <TabsTrigger value="tagget" data-testid="tab-tagget">Tagget meg</TabsTrigger>
          <TabsTrigger value="alle" data-testid="tab-alle">Alle oppgaver</TabsTrigger>
          <TabsTrigger value="apne" data-testid="tab-apne">Åpne</TabsTrigger>
          <TabsTrigger value="forfalte" data-testid="tab-forfalte">Forfalte</TabsTrigger>
          <TabsTrigger value="fullfort" data-testid="tab-fullfort">Fullførte</TabsTrigger>
        </TabsList>
        <TabsContent value="mine"><TaskList params="mine=true&status=apen" refresh={refresh} /></TabsContent>
        <TabsContent value="tagget"><TaskList params="tagged=true" refresh={refresh} /></TabsContent>
        <TabsContent value="alle"><TaskList params="" refresh={refresh} /></TabsContent>
        <TabsContent value="apne"><TaskList params="status=apen" refresh={refresh} /></TabsContent>
        <TabsContent value="forfalte"><OverdueView refresh={refresh} /></TabsContent>
        <TabsContent value="fullfort"><TaskList params="status=ferdig" refresh={refresh} /></TabsContent>
      </Tabs>
    </Page>
  );
}
